/**
 * BlockEventBus - 块引用统一事件总线
 *
 * 设计目标：
 * 1. 单一数据源：以 TanStack DB Collection（blocks 表）为唯一真实来源
 * 2. 单向数据流：DB 变更 → 事件 → 视图更新（视图不直接回写 DB）
 * 3. 零轮询：完全依赖 subscribeChanges + Tauri 事件推送
 * 4. 零循环防护：单向数据流从根本上消除循环
 *
 * 替代：
 * - BlockSubscriptionManager（30 秒轮询 + Tauri 事件 + BroadcastChannel）
 * - UpdateLoopGuard（运行时循环防护补丁）
 * - blockCache Map（Collection 即缓存）
 */

import type { Block, Note } from '@shared/types'
import { getBlocksCollection, getNotesCollection, getBlockReferencesCollection, onBlocksReady, onBlockReferencesReady } from '../store/collections'
import { isTauri } from '@tauri-apps/api/core'

type BlockListener = (block: Block | null) => void
type NoteListener = (note: Note) => void

class BlockEventBus {
  private static instance: BlockEventBus

  /** 块变更监听器：blockId → 监听器集合 */
  private blockListeners = new Map<string, Set<BlockListener>>()
  /** 笔记变更监听器：noteId → 监听器集合 */
  private noteListeners = new Map<string, Set<NoteListener>>()

  /** Collection 订阅句柄 */
  private blocksCollectionSub: { unsubscribe: () => void } | null = null
  private notesCollectionSub: { unsubscribe: () => void } | null = null
  private blockRefsCollectionSub: { unsubscribe: () => void } | null = null

  /** Tauri 事件取消监听函数 */
  private tauriUnlisteners: Array<() => void> = []

  /** 启动状态 */
  private started = false
  /** 启动 Promise（避免重复启动） */
  private startPromise: Promise<void> | null = null

  private constructor() {}

  static getInstance(): BlockEventBus {
    if (!BlockEventBus.instance) {
      BlockEventBus.instance = new BlockEventBus()
    }
    return BlockEventBus.instance
  }

  /**
   * 启动事件总线
   * 订阅 Collection 变更 + 监听 Tauri 事件（跨窗口）
   */
  async start(): Promise<void> {
    if (this.started) return
    if (this.startPromise) return this.startPromise

    this.startPromise = this._doStart()
    await this.startPromise
    this.startPromise = null
  }

  private async _doStart(): Promise<void> {
    try {
      // 1. 订阅 blocks Collection 变更（本地 DB 响应式，单一数据源）
      // blocks 集合后台串行初始化（SQLite 单写者原则），可能晚于本总线启动：
      // 集合未就绪时通过 onBlocksReady 延迟补齐订阅，避免跨设备同步变更丢失通知
      if (!this.subscribeBlocksCollection()) {
        onBlocksReady(() => this.subscribeBlocksCollection())
      }

      // 2. 订阅 notes Collection 变更（用于 note 类型引用）
      try {
        const notesCollection = getNotesCollection()
        if (notesCollection) {
          this.notesCollectionSub = notesCollection.subscribeChanges(
            (changes: any[]) => {
              if (!Array.isArray(changes)) return
              changes.forEach((change: any) => {
                if ((change.type === 'update' || change.type === 'insert') && change.value) {
                  const note = change.value as Note
                  if (note?.id) {
                    this.notifyNoteListeners(note.id, note)
                  }
                }
              })
            },
            { includeInitialState: false }
          )
          console.log('[BlockEventBus] 已订阅 notes Collection 变更')
        }
      } catch (e) {
        console.warn('[BlockEventBus] 订阅 notes Collection 失败:', e)
      }

      // 3. blockReferences 已并入 blocks 集合（Phase3），不再独立订阅——
      //    同一集合重复 subscribeChanges 会导致每个变更被处理两次（双重通知）。
      //    引用增删通过下方 subscribeBlockRefsDeleteOnly 的 delete 变更分支处理
      //    （insert/update 由 blocks 订阅覆盖，其 change.value 含 targetBlockId 等引用字段）。
      if (!this.subscribeBlockRefsDeleteOnly()) {
        onBlockReferencesReady(() => this.subscribeBlockRefsDeleteOnly())
      }

      // 4. 监听 Tauri 事件（跨窗口通知，其他窗口的 DB 变更通过此通道）
      if (isTauri()) {
        try {
          const { listen } = await import('@tauri-apps/api/event')

          // block:updated 事件
          const unlistenBlock = await listen('block:updated', (event: any) => {
            const payload = event.payload || {}
            const blockId = payload.blockId
            if (!blockId) return

            // 从本地 Collection 读取最新数据（单一数据源原则）
            const coll = getBlocksCollection()
            if (coll) {
              const block = coll.get(blockId) as Block | undefined
              if (block) {
                this.notifyBlockListeners(blockId, block)
              }
            }
          })
          this.tauriUnlisteners.push(unlistenBlock)

          // block:created 事件
          const unlistenBlockCreated = await listen('block:created', (event: any) => {
            const payload = event.payload || {}
            const blockId = payload.blockId
            if (!blockId) return

            const coll = getBlocksCollection()
            if (coll) {
              const block = coll.get(blockId) as Block | undefined
              if (block) {
                this.notifyBlockListeners(blockId, block)
              }
            }
          })
          this.tauriUnlisteners.push(unlistenBlockCreated)

          // note:updated 事件
          const unlistenNote = await listen('note:updated', (event: any) => {
            const payload = event.payload || {}
            const noteId = payload.noteId
            if (!noteId) return

            try {
              const coll = getNotesCollection()
              if (coll) {
                const note = coll.get(noteId) as Note | undefined
                if (note) {
                  this.notifyNoteListeners(noteId, note)
                }
              }
            } catch (e) {
              // notesCollection 可能未初始化
            }
          })
          this.tauriUnlisteners.push(unlistenNote)

          // block-reference:created 事件（引用关系创建）
          // 通知目标块的所有订阅者（包括 BlockReferenceCountExtension 重新获取引用计数）
          const unlistenRefCreated = await listen('block-reference:created', (event: any) => {
            const payload = event.payload || {}
            const targetBlockId = payload.targetBlockId
            if (!targetBlockId) return

            const listeners = this.blockListeners.get(targetBlockId)
            if (listeners && listeners.size > 0) {
              // 优先从 blocksCollection 获取完整 block（用于其他可能需要完整 block 的订阅者）
              const coll = getBlocksCollection()
              let block: Block | null = null
              if (coll) {
                block = coll.get(targetBlockId) as Block | undefined || null
              }
              if (!block) {
                // Collection 中暂无该块，使用 stub block（BlockReferenceCountExtension 只需要 id）
                block = { id: targetBlockId } as Block
              }
              this.notifyBlockListeners(targetBlockId, block)
              // 触发全局自定义事件
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('block-ref:changed', { detail: { blockId: targetBlockId } }))
              }
            }
          })
          this.tauriUnlisteners.push(unlistenRefCreated)

          // block-reference:deleted 事件（引用关系删除）
          const unlistenRefDeleted = await listen('block-reference:deleted', (event: any) => {
            const payload = event.payload || {}
            const targetBlockId = payload.targetBlockId
            if (!targetBlockId) return

            const listeners = this.blockListeners.get(targetBlockId)
            if (listeners && listeners.size > 0) {
              const coll = getBlocksCollection()
              let block: Block | null = null
              if (coll) {
                block = coll.get(targetBlockId) as Block | undefined || null
              }
              if (!block) {
                block = { id: targetBlockId } as Block
              }
              this.notifyBlockListeners(targetBlockId, block)
              // 触发全局自定义事件
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('block-ref:changed', { detail: { blockId: targetBlockId } }))
              }
            }
          })
          this.tauriUnlisteners.push(unlistenRefDeleted)

          console.log('[BlockEventBus] 已订阅 Tauri 事件（block:updated/created, note:updated, block-reference:created/deleted）')
        } catch (e) {
          console.warn('[BlockEventBus] 订阅 Tauri 事件失败:', e)
        }
      }

      // 4. 监听 Electric SQL 同步事件（跨设备同步）
      // 注：原 electricSync.ts 已移除，现由 TanStack DB collection.subscribeChanges 驱动；保留监听以兼容历史事件
      const handleElectricSyncUpdate = (event: Event) => {
        const detail = (event as CustomEvent).detail as { entity?: string }
        const entity = detail?.entity
        if (!entity) return

        console.log('[BlockEventBus] 收到 electric:sync:update 事件:', entity)

        if (entity === 'blocks') {
          const coll = getBlocksCollection()
          if (coll) {
            const allBlocks = coll.getAll() as any[]
            allBlocks.forEach(rawBlock => {
              const block: Block = {
                ...rawBlock,
                blockType: rawBlock.blockType || rawBlock.block_type || rawBlock.type || 'PARAGRAPH',
                parentId: rawBlock.parentId || rawBlock.parent_id,
                noteId: rawBlock.noteId || rawBlock.note_id,
              } as Block
              if (block?.id) {
                this.notifyBlockListeners(block.id, block)
              }
            })
          }
        } else if (entity === 'notes') {
          const coll = getNotesCollection()
          if (coll) {
            const allNotes = coll.getAll() as Note[]
            allNotes.forEach(note => {
              if (note?.id) {
                this.notifyNoteListeners(note.id, note)
              }
            })
          }
        }
      }
      window.addEventListener('electric:sync:update', handleElectricSyncUpdate)
      this.tauriUnlisteners.push(() => {
        window.removeEventListener('electric:sync:update', handleElectricSyncUpdate)
      })

      console.log('[BlockEventBus] 已订阅 electric:sync:update 事件（跨设备同步）')

      this.started = true
      console.log('[BlockEventBus] 启动完成')
    } catch (error) {
      console.error('[BlockEventBus] 启动失败:', error)
      this.started = true // 即使失败也标记为已启动，避免重复尝试
    }
  }

  /**
   * 订阅 blocks Collection 变更（幂等）
   * @returns true=已订阅；false=集合未初始化（调用方需注册 onBlocksReady 延迟补齐）
   */
  private subscribeBlocksCollection(): boolean {
    if (this.blocksCollectionSub) return true
    const blocksCollection = getBlocksCollection()
    if (!blocksCollection) {
      console.warn('[BlockEventBus] blocks Collection 未初始化，等待就绪后补齐订阅')
      return false
    }
    try {
      this.blocksCollectionSub = blocksCollection.subscribeChanges(
        (changes: any[]) => {
          if (!Array.isArray(changes)) return
          changes.forEach((change: any) => {
            if ((change.type === 'update' || change.type === 'insert') && change.value) {
              const rawBlock = change.value as any
              const block: Block = {
                ...rawBlock,
                blockType: rawBlock.blockType || rawBlock.block_type || rawBlock.type || 'PARAGRAPH',
                parentId: rawBlock.parentId || rawBlock.parent_id,
                noteId: rawBlock.noteId || rawBlock.note_id,
              } as Block
              if (block?.id) {
                this.notifyBlockListeners(block.id, block)
                // 同时触发全局自定义事件，作为备用机制
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent('block:updated', { detail: { blockId: block.id, block } }))
                }
              }
            } else if (change.type === 'delete' && change.key) {
              // ★ delete 分支：块删除（含他端删除）通知订阅者，避免引用视图展示过期数据
              const blockId = typeof change.key === 'string' ? change.key : (change.key?.id || change.key?.key || null)
              if (blockId) {
                this.notifyBlockListeners(blockId, null)
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent('block:deleted', { detail: { blockId } }))
                }
              }
            }
          })
        },
        { includeInitialState: false }
      )
      console.log('[BlockEventBus] 已订阅 blocks Collection 变更')
      return true
    } catch (e) {
      console.error('[BlockEventBus] 订阅 blocks Collection 失败:', e)
      return false
    }
  }

  /**
   * ★ 只处理 block 引用删除变更（幂等）。
   * Phase3 后 block_references 已并入 blocks 集合，本订阅与 subscribeBlocksCollection
   * 是同一集合，因此只处理 delete 类型（insert/update 由 blocks 订阅覆盖），
   * 避免同一变更被两个回调各处理一次导致双重通知。
   */
  private subscribeBlockRefsDeleteOnly(): boolean {
    if (this.blockRefsCollectionSub) return true
    const blockRefsCollection = getBlockReferencesCollection()
    if (!blockRefsCollection) {
      console.warn('[BlockEventBus] blockReferencesCollection 为 null，等待就绪后补齐订阅')
      return false
    }
    try {
      this.blockRefsCollectionSub = blockRefsCollection.subscribeChanges(
        (changes: any[]) => {
          if (!Array.isArray(changes)) return
          const affectedBlockIds = new Set<string>()
          changes.forEach((change: any) => {
            // 只处理 delete（insert/update 由 blocks 订阅处理，避免双重通知）
            if (change.type !== 'delete') return
            const ref = change.oldValue || change.value
            if (!ref) return
            const targetBlockId =
              ref.targetBlockId ||
              ref.target_block_id ||
              ref.targetBlockID ||
              ref.targetId
            if (targetBlockId) {
              affectedBlockIds.add(targetBlockId)
            }
          })
          // 通知受影响的块的订阅者（引用被删除 → 小圆点/计数刷新）
          affectedBlockIds.forEach(blockId => {
            try {
              const listeners = this.blockListeners.get(blockId)
              if (listeners && listeners.size > 0) {
                const stubBlock = { id: blockId } as Block
                this.notifyBlockListeners(blockId, stubBlock)
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent('block-ref:changed', { detail: { blockId } }))
                }
              }
            } catch (e) {
              console.warn('[BlockEventBus] 通知 block 监听器失败:', blockId, e)
            }
          })
        },
        { includeInitialState: false }
      )
      console.log('[BlockEventBus] 已订阅 blocks 引用删除变更（delete-only）')
      return true
    } catch (e) {
      console.error('[BlockEventBus] 订阅 blocks 引用删除变更失败:', e)
      return false
    }
  }

  /**
   * 订阅特定块的变化
   * @param blockId 块 ID
   * @param listener 监听器函数，接收 Block 对象
   * @returns 取消订阅函数
   */
  subscribeBlock(blockId: string, listener: BlockListener): () => void {
    if (!this.blockListeners.has(blockId)) {
      this.blockListeners.set(blockId, new Set())
    }
    this.blockListeners.get(blockId)!.add(listener)

    return () => {
      this.blockListeners.get(blockId)?.delete(listener)
      if (this.blockListeners.get(blockId)?.size === 0) {
        this.blockListeners.delete(blockId)
      }
    }
  }

  /**
   * 订阅特定笔记的变化
   * @param noteId 笔记 ID
   * @param listener 监听器函数，接收 Note 对象
   * @returns 取消订阅函数
   */
  subscribeNote(noteId: string, listener: NoteListener): () => void {
    if (!this.noteListeners.has(noteId)) {
      this.noteListeners.set(noteId, new Set())
    }
    this.noteListeners.get(noteId)!.add(listener)

    return () => {
      this.noteListeners.get(noteId)?.delete(listener)
      if (this.noteListeners.get(noteId)?.size === 0) {
        this.noteListeners.delete(noteId)
      }
    }
  }

  /**
   * 从本地 Collection 获取块数据（单一数据源）
   * @param blockId 块 ID
   * @returns 块对象或 undefined
   */
  getBlock(blockId: string): Block | undefined {
    const coll = getBlocksCollection()
    if (!coll) return undefined
    try {
      return coll.get(blockId) as Block | undefined
    } catch {
      return undefined
    }
  }

  /**
   * 从本地 Collection 获取笔记数据
   */
  getNote(noteId: string): Note | undefined {
    try {
      const coll = getNotesCollection()
      if (!coll) return undefined
      return coll.get(noteId) as Note | undefined
    } catch {
      return undefined
    }
  }

  /**
   * 手动通知块更新（用于 IPC 回退路径等非 Collection 触发场景）
   * 确保所有订阅者收到更新通知
   */
  notifyBlockUpdate(blockId: string, block: Block): void {
    this.notifyBlockListeners(blockId, block)
  }

  /**
   * 通知块监听器
   * 内部方法，由 Collection 订阅和 Tauri 事件触发
   */
  private notifyBlockListeners(blockId: string, block: Block | null): void {
    const listeners = this.blockListeners.get(blockId)
    if (!listeners || listeners.size === 0) return

    listeners.forEach(cb => {
      try {
        cb(block)
      } catch (e) {
        console.error('[BlockEventBus] 块监听器执行失败:', e)
      }
    })
  }

  /**
   * 通知笔记监听器
   */
  private notifyNoteListeners(noteId: string, note: Note): void {
    const listeners = this.noteListeners.get(noteId)
    if (!listeners || listeners.size === 0) return

    listeners.forEach(cb => {
      try {
        cb(note)
      } catch (e) {
        console.error('[BlockEventBus] 笔记监听器执行失败:', e)
      }
    })
  }

  /**
   * 获取当前活跃订阅数（用于调试）
   */
  getSubscriptionStats(): { blocks: number; notes: number } {
    return {
      blocks: this.blockListeners.size,
      notes: this.noteListeners.size,
    }
  }

  /**
   * 销毁事件总线，释放所有资源
   */
  destroy(): void {
    if (this.blocksCollectionSub) {
      try {
        this.blocksCollectionSub.unsubscribe()
      } catch (e) {
        console.warn('[BlockEventBus] 取消 blocks Collection 订阅失败:', e)
      }
      this.blocksCollectionSub = null
    }

    if (this.notesCollectionSub) {
      try {
        this.notesCollectionSub.unsubscribe()
      } catch (e) {
        console.warn('[BlockEventBus] 取消 notes Collection 订阅失败:', e)
      }
      this.notesCollectionSub = null
    }

    if (this.blockRefsCollectionSub) {
      try {
        this.blockRefsCollectionSub.unsubscribe()
      } catch (e) {
        console.warn('[BlockEventBus] 取消 blockReferences Collection 订阅失败:', e)
      }
      this.blockRefsCollectionSub = null
    }

    this.tauriUnlisteners.forEach(unlisten => {
      try {
        unlisten()
      } catch (e) {
        console.warn('[BlockEventBus] 取消 Tauri 事件监听失败:', e)
      }
    })
    this.tauriUnlisteners = []

    this.blockListeners.clear()
    this.noteListeners.clear()
    this.started = false
    this.startPromise = null
  }
}

/** 单例实例 */
export const blockEventBus = BlockEventBus.getInstance()

export { BlockEventBus }
